import { EditOutlined, LeftOutlined, MinusSquareOutlined, RightOutlined, SafetyOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Flex, Input, Modal, Row, Select, Space, Spin, Table, Tag, Tooltip, Typography, theme } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { GEO_LIST_TREE_STRUCTURE, region_title_index_map } from "../../../../../../constants";
import BulkRegionMappingModal from "./BulkRegionMappingModal";
import { IAB_CHOICES_SYSTEM_IDS, LEGALBASIS_OPTIONS, renderInfoTip, ACTIVE_STATE_LAW_CODES, StateCodeRender, PRIVACY_STRING_TOOLTIP, USNAT_CODE, PRIVACY_STRING_CHANGE_STATE_TO_NAT_TOOLTIP, PRIVACY_STRING_CHANGE_NAT_TO_STATE_TOOLTIP } from "../../../helper";
import { useDebouncedSearch } from "../../../../../../hooks";
import RespectGpcModal from "./RespectGpcModal";
import { useSelector } from "react-redux";

const {Title, Text, Paragraph} = Typography;

const RegionMapping = (props) => {
  const [categories, setCategories] = useState(props.usPrivacyRegulation.categories ?? []);
  const [privacySignalConfigByGeo, setPrivacySignalConfigByGeo] = useState(props.usPrivacyRegulation.configByGeo);
  const [regionCategoryMapping, setRegionCategoryMapping] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [openBulkModal, setOpenBulkModal] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [openRespectGpcModal, setOpenRespectGpcModal] = useState(false);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(true);
  const [tableState, setTableState] = useState([]);
  const systemPurposes = useSelector(state => state.usPrivacyReducerState.getIn(['purposes', 'value']))
  const systemPurposesPending = useSelector(state => state.usPrivacyReducerState.getIn(['purposes', 'pending']));
  const stateStringRegions = useSelector(state => state.usPrivacyReducerState.getIn(['stateStringRegions', 'value']))
  const { token } = theme.useToken();
  const [modal, contextHolder] = Modal.useModal();
  const usnatRegionsToUpdate = useMemo(() => regionCategoryMapping.reduce((acc,curr) => {
    if(!acc?.includes(curr.region?.split("_",1)[0]) && curr.privacySignal === "usnat") {
      acc.push(curr.region?.split("_",1)[0])
    }
    return acc;
  },[]),[regionCategoryMapping]);

  useEffect(() => {
    props.usPrivacyRegulation.categories = categories;
    props.usPrivacyRegulation.configByGeo = privacySignalConfigByGeo
  }, [categories, privacySignalConfigByGeo])

  function getIabDefinedConfigGeo(cat, region, privacySignal) {
    const iabPurposeOriginal = systemPurposes.find((purpose) => purpose.purposeRef.id === cat.purposeRef?.id)
    let regionCode = region;
    if((privacySignal ?? privacySignalConfigByGeo?.[region]?.legislation) === "usnat") {
      regionCode = USNAT_CODE//record.region?.split("_",1)[0]
    }
    const iabPurOrgConfigGeo = iabPurposeOriginal?.configByGeo[regionCode]
    return iabPurOrgConfigGeo;
  };

  async function handleLegalBasisChange(categoryId, regionCode, legalBasisValue) {
    if(privacySignalConfigByGeo[regionCode]?.legislation === "usnat" && !props.isNoFrameworkFlow) {
      const confirmation = await modal.confirm({
        title:"Consent Update",
        content: "Since the national string has only one string, we will be applying this consent change to all the regions with National String for this privacy choice. Are you sure you want to go ahead with this change?",
        okText: "Apply",
        width: 500,
      })
      if(confirmation) {
        setCategories((categories) => categories.map((cat) => {
          if(cat.id === categoryId) {
            usnatRegionsToUpdate.forEach((region) => {
              cat.configByGeo[region] = {defaultLegalBasis: legalBasisValue};
            })
          } 
          return cat;
        }))
      }
    } else {
      setCategories((categories) =>categories.map((cat) => {
        if(cat.id === categoryId) {
          cat.configByGeo[regionCode] = {defaultLegalBasis: legalBasisValue};
        } 
        return cat;
      }))
    }
  }

  async function handlePrivacySignalChange(regionCode, privacySignalValue) {
    let modalContent = privacySignalValue === "usnat" ? PRIVACY_STRING_CHANGE_STATE_TO_NAT_TOOLTIP : PRIVACY_STRING_CHANGE_NAT_TO_STATE_TOOLTIP;
    const confirmation =
      privacySignalValue === "usnat" && props.previousLegislation === "usnat" && props.usPrivacyRegulation.legislation === "hybrid"
        ? true
        : await modal.confirm({
            title: "Consent Update",
            content: modalContent,
            okText: "Apply",
            width: 500,
          });
    if(confirmation) {
      setPrivacySignalConfigByGeo(prevConfig => {
        const newConfig = {...prevConfig}; // Create a new object with spread
        newConfig[regionCode] = {legislation: privacySignalValue}
        return newConfig; // Return the new object
      });
      if(privacySignalValue === "usnat" && !props.isNoFrameworkFlow) {
        setCategories((categories) => categories.map((cat) => {  
          if(cat.configByGeo[regionCode]) {
            // cat.configByGeo[regionCode?.split("_", 1)[0]] = cat.configByGeo[regionCode];
            delete cat.configByGeo[regionCode];
          }
          return cat;
        }))
      } else {
        setCategories((categories) => categories.map((cat) => {
          let iabConfigGeo = getIabDefinedConfigGeo(cat, regionCode, "state");
          cat.configByGeo[regionCode] = iabConfigGeo ?? {defaultLegalBasis: "NOT_APPLICABLE"}
          return cat;
        }))
      }
    }
  }


  const columns = [
    {
      title: "Region",
      fixed: "left",
      dataIndex: "region",
      width: 200,
      sorter: (a,b) => a.title?.localeCompare(b.title),
      filters: props.isNoFrameworkFlow ? null : [
        {
          text: <Typography.Text><img src="/svg_icons/trademark-s.svg" style={{marginBottom: 2}}/> - Supports State String {renderInfoTip("A state that has a state string supported by the Global Privacy Platform")}</Typography.Text>,
          value: "state-string"
        },
        {
          text: <Typography.Text><img src="/svg_icons/trademark-a.svg" style={{marginBottom: 2}}/> - Active State Law {renderInfoTip("A state with an effective privacy legislation")}</Typography.Text>,
          value: "active-law"
        },
        {
          text:  <Typography.Text>All other regions</Typography.Text>,
          value: "other"
        }
      ],
      filterMultiple: false,
      onFilter: (value, record) => {
        const isActiveStateLaw = ACTIVE_STATE_LAW_CODES.includes(record.region.replace("US_", "STATE_"));
        const isStateString = stateStringRegions[record?.region];
        switch(value) {
          case "active-law":
            return isActiveStateLaw;
          case "state-string":
            return isStateString;
          default:
            return (!isActiveStateLaw && !isStateString)
        }
      },
      render: (stateCode, record) => {
        return (
          <Flex gap={token.marginSM} align="center">
            <Text>{record.title}</Text>
            {props.isNoFrameworkFlow ? null : <StateCodeRender stateCode={stateCode.replace("US_", "STATE_")}/>}
          </Flex>
        );
      },
    }
  ]
  if(!props.isNoFrameworkFlow) {
    columns.push(
      {
        title: <>Privacy String {renderInfoTip(PRIVACY_STRING_TOOLTIP, null, "4px", 800)}</>,
        fixed: "left",
        dataIndex: "privacySignal",
        width: 200,
        filters: [
          {text: "National", value: "usnat"},
          {text: "State", value: "state"}
        ],
        onFilter: (value, record) => value === record.privacySignal,
        filterMultiple: false,
        render: (value, record) => {
          let options = [{label: "National", value: "usnat"}];
          if(stateStringRegions[record?.region]) {
            options.push({label: "State", value: "state"})
          }
          return (
            <Select
              value={value}
              defaultValue={"usnat"}
              placeholder="Select"
              options={options}
              disabled={props.readOnly}
              onChange={(value) => handlePrivacySignalChange(record.region, value)}
              style={{minWidth: 150}}
            />
          )
        }
      }
    )
  }

  categories.forEach((cat) => {
    const isIabCat = cat.type === "SYSTEM_PURPOSE"
    let options = LEGALBASIS_OPTIONS;
    columns.push({
      title: (
        <Space direction="vertical">
          <Paragraph style={{maxWidth: 150}} ellipsis={{tooltip:{title: cat.privacyChoice ?? cat.name}, rows: 2}}>{cat.privacyChoice ?? cat.name}</Paragraph>
          <Tag color={isIabCat ? "orange" : "magenta"}> {isIabCat ? props.isNoFrameworkFlow ? "Standard" : "IAB" : "Custom"}</Tag>
        </Space>
      ),
      dataIndex: ["legalBasis", cat.id],
      width: 200,
      sorter: (a,b) => {
        const legalBasisOrder = options.reduce((acc, option, index) => {
          acc[option.value] = index;
          return acc;
        }, {});
        const aOrder = legalBasisOrder[a.legalBasis[cat.id]] ?? 3; // 3 is the index for not_applicable
        const bOrder = legalBasisOrder[b.legalBasis[cat.id]] ?? 3;
        return aOrder - bOrder;
      },
      showSorterTooltip: {target: 'sorter-icon'},
      render: (value, record) => {
        let isDisabled = false;
        let orgIabConfig = null;
        if(!props.isNoFrameworkFlow && isIabCat) {
          orgIabConfig = getIabDefinedConfigGeo(cat, record.region);
          isDisabled = (!orgIabConfig || orgIabConfig?.defaultLegalBasis === "NOT_APPLICABLE")
        }
        return (
          <Tooltip
            title={isDisabled ? "Changing this not applicable in this region" : null}
          >
            <Select
              value={value ?? "NOT_APPLICABLE"}
              defaultValue={!Boolean(value) ? "NOT_APPLICABLE" : null}
              placeholder="Select"
              options={!props.isNoFrameworkFlow && orgIabConfig ? options.filter((opt) => orgIabConfig?.defaultLegalBasis === opt.value || opt.value === "NOT_APPLICABLE") : options}
              disabled={isDisabled || props.readOnly}
              onChange={(value) => handleLegalBasisChange(cat.id, record.region, value)}
              style={{minWidth: 150}}
            />
          </Tooltip>
        )
      }
    })
  })

  const handleTableChange = (pagination, filters, sorter, extra) => {
    setTableState({pagination,filters,sorter, extra});
    if(extra.action == "filter") {
      setSelectedRowKeys([])
    }
  };

  useEffect (() => {
    let selectedRegionCodes = []
    GEO_LIST_TREE_STRUCTURE.forEach((option) => {
      if (props.usPrivacyRegulation.appliesGeos.includes(option.key)) {
        const childCodes = option.children.map(c => c.code);
        const filteredChildCodes = childCodes.filter(code => !props.usPrivacyRegulation.exclusionGeos.includes(code));
        selectedRegionCodes = selectedRegionCodes.concat(filteredChildCodes);
      } else {
        const childCodes = option.children.map(c => c.code);
        const filteredChildCodes = childCodes.filter(code => props.usPrivacyRegulation.appliesGeos.includes(code) && !props.usPrivacyRegulation.exclusionGeos.includes(code));
        selectedRegionCodes = selectedRegionCodes.concat(filteredChildCodes)
      }
    });
    let regionCategoryMapping = selectedRegionCodes.map((code) => {
      let codeString = code?.replace("STATE_", "US_")
      return ({region: codeString, key: codeString, title: region_title_index_map[code], legalBasis: {}, privacySignal: privacySignalConfigByGeo[codeString]?.legislation ?? "usnat"})
    });
    categories.forEach((cat) => {
      if(cat.configByGeo) {
        regionCategoryMapping = regionCategoryMapping.map((data) => {
          let defLegBasisRecord = cat.configByGeo[data.region]
          if(data.privacySignal === "usnat" && !props.isNoFrameworkFlow) {
            defLegBasisRecord = cat.configByGeo[data.region?.split("_",1)[0]]
          }
          //<workaround for unexpected VL record update bug
          if(defLegBasisRecord?.disclosureOnly) {
            defLegBasisRecord.defaultLegalBasis = "DISCLOSURE_ONLY"
          }
          //workaround>
          if(defLegBasisRecord) {
            data["legalBasis"][cat.id] = defLegBasisRecord.defaultLegalBasis
          }
          return data;
        })
      } 
    })
    setRegionCategoryMapping(regionCategoryMapping);
  }, [categories, privacySignalConfigByGeo]);

  const rowSelection = {
    selectedRowKeys,
    onSelect : (record, selected, selectedRows, nativeEvent) => {
      if(selected) {
        setSelectedRowKeys((selectedKeys) => [... new Set(selectedKeys.concat(record.region))])
      } else {
        setSelectedRowKeys((selectedKeys) => selectedKeys.filter(key => key !== record.region))
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      let datasource = tableState.filters ? tableState.extra.currentDataSource : regionCategoryMapping;
      if(selected){
        setSelectedRowKeys((selectedKeys) => [...new Set(selectedKeys.concat(datasource.map(v => v.region)))]);
      }else{
        setSelectedRowKeys((selectedKeys) => selectedKeys.filter((key) => !datasource.map(v => v.region).includes(key)));
      }
    },
  }

  function handleClearSelection() {
    setSelectedRowKeys([]);
  }

  function handleBulkEdit() {
    setOpenBulkModal(true);
  }

  function handleGpcEdit() {
    setOpenRespectGpcModal(true);
  }

const scrollTable = (scrollBy) => {
    const tableBody = document.querySelector('.usnat-region-mapping .ant-table-content');
    if (tableBody) {
      tableBody.scrollTo({
        left: tableBody.scrollLeft + scrollBy,
        behavior: 'smooth'
      });
    }
  };

  const updateButtonState = () => {
    const tableBody = document.querySelector('.usnat-region-mapping .ant-table-content');
    if (tableBody) {
      const maxScrollLeft = tableBody.scrollWidth - tableBody.clientWidth;

      setIsLeftDisabled(tableBody.scrollLeft <= 0);
      setIsRightDisabled(tableBody.scrollLeft >= maxScrollLeft - 1);
    }
  };

 useEffect(() => {
    if (props.usPrivacyRegulation?.appliesGeos?.length === 0) return;

    // Using setTimeout to ensure table is mounted
    const timeoutId = setTimeout(() => {
      const tableBody = document.querySelector('.usnat-region-mapping .ant-table-content');
      if (tableBody) {
        tableBody.addEventListener('scroll', updateButtonState);
        updateButtonState();
      }
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      const tableBody = document.querySelector('.usnat-region-mapping .ant-table-content');
      if (tableBody) {
        tableBody.removeEventListener('scroll', updateButtonState);
      }
    };
  }, [props.usPrivacyRegulation?.appliesGeos?.length]);


  const debouncedChangeHandler = useDebouncedSearch(setSearchValue, 700);

  let dataSource = regionCategoryMapping
  if(searchValue.trim().length) {
    dataSource = regionCategoryMapping?.filter(record => record.title?.toLowerCase().indexOf(searchValue.trim().toLowerCase()) !== -1)
  }

  const gpcButton = categories.some((cat) => cat.purposeRef?.systemId === IAB_CHOICES_SYSTEM_IDS.saleAndSharing) ?  <Button type="primary" icon={<SafetyOutlined/>} onClick={handleGpcEdit}>GPC</Button> : null;

  return systemPurposesPending ? <Spin/> :  (
    <>
      {props.changesDetectedWarning}
      <Row gutter={[0, token.margin]}>
        <Col span={24}>
          {!props.readOnly ? (
            <Space direction="vertical" size={token.marginXXS}>
              <Title level={4}>Region Mapping</Title>
              <Text>
                {props.isNoFrameworkFlow
                  ? "We have configured the default consent options for privacy choices on a per state basis, based on the latest legislation. You can make modifications using the drop-down menus in the matrix below."
                  : "You can modify the consent options for different regions, strings and privacy choices using the dropdowns in the matrix below. For IAB privacy choices, we offer the options defined by the framework"}
              </Text>
            </Space>
          ) : null}
        </Col>
        <Col span={8}>
          <Input.Search
            placeholder="search"
            type="search"
            onChange={({ target: { value } }) => {
              debouncedChangeHandler(value);
            }}
          />
        </Col>
        <Col span={8} offset={8}>
          <Flex align='center' justify="flex-end">
            <Space size={token.margin}>
              {selectedRowKeys.length 
              ? (
                <>
                  <Button type="link" icon={<MinusSquareOutlined/>} onClick={handleClearSelection} disabled={props.readOnly}> Clear Selection</Button>
                  <Button type="primary" icon={<EditOutlined/>} onClick={handleBulkEdit} disabled={!selectedRowKeys.length || props.readOnly}> Bulk Edit ({selectedRowKeys.length})</Button>
                </>
              ) : gpcButton}
            </Space>
            {Boolean(gpcButton) || selectedRowKeys.length ? <Divider type="vertical" /> : null}
            <Space size={token.margin}>
              <Button  disabled={isLeftDisabled} icon={<LeftOutlined/>} onClick={() => scrollTable(-1000)}></Button>
              <Button  disabled={isRightDisabled} icon={<RightOutlined/>} onClick={() => scrollTable(1000)}></Button>
            </Space>
          </Flex>
        </Col>
        <Col span={24}>
          <Table
            onChange={handleTableChange}
            columns={columns}
            dataSource={dataSource}
            className="usnat-region-mapping"
            rowSelection={!props.readOnly ? rowSelection : null}
            scroll={{ x: 1000}}
            pagination={{
              position: ['bottomCenter'],
              showTotal: (total) => `Total ${total} items`,
              size:'small',
              defaultPageSize: props.readOnly ? 6 : 7,
              showSizeChanger: true,
              pageSizeOptions: ['7', '10', '20', '50'],
            }}
          />
        </Col>
      </Row>
      {openBulkModal ? (
        <BulkRegionMappingModal
          selectedRegions={regionCategoryMapping.filter(({region}) => selectedRowKeys.includes(region))}
          usnatRegionsToUpdate={usnatRegionsToUpdate}
          categories={categories}
          privacySignalConfigByGeo={privacySignalConfigByGeo}
          setCategories={setCategories}
          setPrivacySignalConfigByGeo={setPrivacySignalConfigByGeo}
          openModal={openBulkModal}
          closeModal={() => setOpenBulkModal(false)}
          isNoFrameworkFlow={props.isNoFrameworkFlow}
          stateStringRegions={stateStringRegions}
          getIabDefinedConfigGeo={getIabDefinedConfigGeo}
          previousLegislation={props.previousLegislation}
        />
      ) : null}
      {openRespectGpcModal ? (
        <RespectGpcModal
          respectGpcCategory={categories.find((cat) => cat.purposeRef?.systemId === IAB_CHOICES_SYSTEM_IDS.saleAndSharing) ?? {respectGPC: null}}
          regionsInVendorList={regionCategoryMapping.map((opt) => opt.region)}
          setCategories={setCategories}
          openModal={openRespectGpcModal}
          closeModal={() => setOpenRespectGpcModal(false)}
          readOnly={props.readOnly}
          isNoFrameworkFlow={props.isNoFrameworkFlow}
        />
      ) : null}
      {contextHolder}
    </>
  );
}

export default RegionMapping;